import contentRoutes from "@/constants/content.routes.js";

/** Fisher-Yates sobre uma cópia, para não mutar o pool recebido. */
const shuffle = (input) => {
    const array = [...input];
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
};

/**
 * Sorteio local dos recomendados da Home, a partir de `global_summary`.
 * Zero rede: recebe o pool já carregado e devolve os itens escolhidos.
 *
 * Regras:
 *   - só entram itens com rota resolvível;
 *   - produtos comerciais ficam de fora (o app os classifica por `type`,
 *     mesma checagem de `Items.vue`); recomendamos conteúdo, não marca;
 *   - tipos distintos primeiro (Medicamento, Doença, Calculadora, ...);
 *   - usuário gratuito recebe no máximo um item PRO por sorteio;
 *   - favoritos e o último conteúdo aberto ficam de fora;
 *   - "Ver outros" evita repetir o sorteio anterior enquanto houver alternativas.
 *
 * @param {object} args
 * @param {Array} args.pool itens de `global_summary`
 * @param {number} args.count quantidade desejada
 * @param {boolean} args.isPremium assinante não tem limite de itens PRO
 * @param {Set<string>} args.excludedKeys chaves de conteúdo a excluir
 * @param {Set<string>} args.previousKeys chaves do sorteio anterior
 * @returns {Array} itens escolhidos, cada um com a flag `pro`
 */
export const pickRecommendations = ({
    pool,
    count,
    isPremium,
    excludedKeys = new Set(),
    previousKeys = new Set(),
}) => {
    const candidates = [];
    for (const raw of (pool || [])) {
        if (!raw || !raw.id || !raw.name) continue;
        if (contentRoutes.getContentKind(raw) === 'product') continue;
        if (!contentRoutes.resolveContentPath(raw)) continue;
        const key = contentRoutes.getContentKey(raw);
        if (!key || excludedKeys.has(key)) continue;
        candidates.push({
            raw,
            key,
            label: contentRoutes.getContentTypeLabel(raw) || 'Conteúdo',
            pro: contentRoutes.isProContent(raw),
        });
    }
    if (!candidates.length) return [];

    const fresh = candidates.filter((c) => !previousKeys.has(c.key));
    const usable = fresh.length >= count ? fresh : candidates;

    const byLabel = new Map();
    for (const candidate of shuffle(usable)) {
        if (!byLabel.has(candidate.label)) byLabel.set(candidate.label, []);
        byLabel.get(candidate.label).push(candidate);
    }

    const labels = shuffle([...byLabel.keys()]);
    const maxPro = isPremium ? count : 1;
    const picked = [];
    const pickedKeys = new Set();
    let proCount = 0;

    const takeFrom = (list) => {
        for (const candidate of list) {
            if (pickedKeys.has(candidate.key)) continue;
            if (candidate.pro && proCount >= maxPro) continue;
            picked.push(candidate);
            pickedKeys.add(candidate.key);
            if (candidate.pro) proCount += 1;
            return true;
        }
        return false;
    };

    // 1ª passada: um item por tipo distinto.
    for (const label of labels) {
        if (picked.length >= count) break;
        takeFrom(byLabel.get(label));
    }
    // 2ª passada: completa os slots restantes com o que sobrou.
    for (const label of labels) {
        while (picked.length < count && takeFrom(byLabel.get(label))) { /* completa os slots */ }
        if (picked.length >= count) break;
    }

    return picked.map((candidate) => ({ ...candidate.raw, pro: candidate.pro }));
};

export default { pickRecommendations };
